import { useMsal } from "@azure/msal-react";
import { useCallback, useRef } from "react";
import {
  AccountInfo,
  BrowserAuthError,
  BrowserAuthErrorMessage,
  IPublicClientApplication,
} from "@azure/msal-browser";
import { loginRequest } from "../../AuthConfig";
import { DataError } from "../../core/errors/DataError";
import { ErrorStatus } from "../../core/models/entities/ErrorStatus";
import { NoCurrentAccountError } from "../../core/errors/NoCurrentAccountError";
import { errorStatusFromBody } from "../models/mappers/mapError";
import { enviroment } from "../../enviroment";
import {
  withClientCorrelationHeaders,
  withExtraHeaders,
} from "./clientCorrelation";

interface AuthorizedFetch {
  // extraHeaders: per-request headers ADDED to the ones authFetch owns
  // (Authorization, correlation) - a caller must never put `headers` in its
  // init (that would replace Authorization, see ApiClient.authHeaders.test).
  // Absent or empty = the init is not touched (today's request exactly).
  authFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
    extraHeaders?: Record<string, string>
  ): Promise<Response>;
}

// B2C can hang for 90+ seconds (observed 04/07-2026); MSAL's silent renewal has
// no timeout of its own, so one hanging renewal froze every API call in the tab
// until the user reloaded. Bound the wait and fall back to a full redirect (an
// automatic "F5") instead.
const SILENT_TOKEN_TIMEOUT_MS = 12000;

class SilentTokenTimeoutError extends Error {
  constructor() {
    super(`acquireTokenSilent timed out after ${SILENT_TOKEN_TIMEOUT_MS} ms`);
    this.name = "SilentTokenTimeoutError";
  }
}

// Module scope: shared by every hook instance so at most ONE redirect is
// started no matter how many parallel API calls hit the timeout together
// (MSAL memoizes identical silent requests, so they all hang and expire
// almost simultaneously).
let tokenRedirectStarted = false;

type TokenRequest = typeof loginRequest & { account: AccountInfo };

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SilentTokenTimeoutError()), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function isInteractionInProgressError(e: unknown): boolean {
  return (
    e instanceof BrowserAuthError &&
    e.errorCode === BrowserAuthErrorMessage.interactionInProgress.code
  );
}

// Deliberately never settles: the page is about to navigate away (full
// redirect = the automatic "F5"), so pending API calls must not proceed
// without a token nor flash error UI while navigation completes.
function never(): Promise<never> {
  return new Promise<never>(() => {});
}

function redirectForToken(
  instance: IPublicClientApplication,
  request: TokenRequest
): Promise<never> {
  if (!tokenRedirectStarted) {
    tokenRedirectStarted = true;
    // Reuse the existing deep-link restore (read by PreLoader in App.tsx) so
    // the user lands back on the page they were on after the round-trip.
    sessionStorage.setItem(
      "redirectOnLogin",
      window.location.pathname + window.location.search
    );
    instance.acquireTokenRedirect(request).catch((e) => {
      // Another interaction already owns the redirect - that one will heal us.
      if (!isInteractionInProgressError(e)) {
        tokenRedirectStarted = false; // allow a fresh attempt later
      }
    });
  }
  return never();
}

async function acquireAccessToken(
  instance: IPublicClientApplication,
  request: TokenRequest
): Promise<string> {
  try {
    const result = await withTimeout(
      instance.acquireTokenSilent(request),
      SILENT_TOKEN_TIMEOUT_MS
    );
    if (result.accessToken) return result.accessToken;
    // Silent succeeded but without a usable token: force a fresh login.
    return redirectForToken(instance, request);
  } catch (error) {
    if (isInteractionInProgressError(error)) {
      return never();
    }
    // Timeout, InteractionRequiredAuthError or any other silent failure.
    // NOTE: retrying silent here is pointless - MSAL memoizes identical
    // requests and would hand back the same hanging promise.
    return redirectForToken(instance, request);
  }
}

export function useAuthorizedFetch(): AuthorizedFetch {
  const { instance, accounts } = useMsal();

  // Admin perf FE-2 (flag adminPerfNavMemoEnabled): ONE authFetch identity
  // per MSAL instance, so useApiClient (and through it every repository) can
  // be memoised instead of rebuilding ~230 closures on every render of the
  // provider chain. The account is read through a ref AT CALL TIME - the ref
  // is assigned during render, so a child effect that fires in the very
  // commit where accounts[0] first appears (login) sees the new account, not
  // a stale one. Token acquisition is unchanged: still per call. Flag off =
  // today's per-render function.
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const memoAuthFetch = useCallback(
    (
      input: RequestInfo | URL,
      init?: RequestInit,
      extraHeaders?: Record<string, string>
    ) =>
      authorizedFetch(
        instance,
        accountsRef.current[0],
        input,
        init,
        extraHeaders
      ),
    [instance]
  );

  async function authFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
    extraHeaders?: Record<string, string>
  ): Promise<Response> {
    return authorizedFetch(instance, accounts[0], input, init, extraHeaders);
  }

  return {
    authFetch: enviroment.adminPerfNavMemoEnabled ? memoAuthFetch : authFetch,
  };
}

// The request itself (unchanged): token per call, Authorization header, the
// caller's init spread on top, correlation headers, the caller's extra
// headers (only when it passed some), error mapping.
async function authorizedFetch(
  instance: IPublicClientApplication,
  currentAcc: AccountInfo | undefined,
  input: RequestInfo | URL,
  init?: RequestInit,
  extraHeaders?: Record<string, string>
): Promise<Response> {
  {
    if (!currentAcc) {
      throw new NoCurrentAccountError(
        "Cannot authorize request if no account is present"
      );
    }

    const request: TokenRequest = {
      ...loginRequest,
      account: currentAcc,
    };

    const accessToken = await acquireAccessToken(instance, request);

    let requestInit: RequestInit = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    if (init !== undefined) {
      requestInit = { ...requestInit, ...init };
    }

    // Client correlation (flag): x-km-session + x-km-click on top of whatever
    // headers the merge above produced, so a caller's own headers object is
    // extended, not replaced. Flag off = the init is not touched at all.
    if (enviroment.clientCorrelationEnabled) {
      requestInit = {
        ...requestInit,
        headers: withClientCorrelationHeaders(requestInit.headers),
      };
    }

    // Per-request extra headers (x-km-op from prefetchDesigns, flag
    // designPrefetchLeanEnabled): added on top of the headers built above,
    // never replacing them. None passed (every other caller) = the init is
    // not touched at all.
    if (extraHeaders && Object.keys(extraHeaders).length > 0) {
      requestInit = {
        ...requestInit,
        headers: withExtraHeaders(requestInit.headers, extraHeaders),
      };
    }

    const response = await fetch(input, requestInit);

    // If bad response throw DataError containing custom error code from backend if any.
    // Error bodies come in three shapes: the JSON envelope { code, message }, plain
    // string bodies (BadRequestObjectResult("...") — JSON-encoded or text/plain) and
    // other JSON objects ({ error }, { disabled, flag }). Read as text first so a
    // non-JSON body surfaces as the DataError message instead of exploding into a
    // SyntaxError from response.json(); errorStatusFromBody keeps the envelope's
    // result unchanged and gives the other shapes the HTTP status (+ the DISABLED
    // marker for flag-gated routes) instead of code/message undefined.
    if (!response.ok) {
      const bodyText = await response.text();
      const errorStatus: ErrorStatus = errorStatusFromBody(
        response.status,
        bodyText
      );
      throw new DataError(errorStatus.message, errorStatus);
    }

    return response;
  }
}
